/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/modular-form/fields/ClienteSearchField.tsx
 * Release: v1.0.0
 * Descrizione: Campo ricerca cliente con QR, targa e dati anagrafici
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import UserCreateModal from '../../admin/UserCreateModal';
import {
  Phone,
  MapPin,
  Building2,
  Eye,
  Mail,
  Home,
  CreditCard,
  Hash,
  User,
  XCircle,
  UserCheck,
  Pencil
} from 'lucide-react';
// import { debounce } from 'lodash';

// Simple debounce implementation to avoid lodash dependency
function debounce<T extends (...args: any[]) => any>(func: T, delay: number): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
}

interface Cliente {
  id: string;
  source?: 'definitive' | 'temporary';
  auth_email: string;
  profile_email?: string;
  name?: string;
  surname?: string;
  customer_type?: 'company' | 'individual';
  company_name?: string;
  vat_number?: string;
  tax_code?: string;
  phone?: string;
  phone_prefix?: string;
  city?: string;
  province?: string;
  street?: string;
  postal_code?: string;
  pec?: string;
  unique_code?: string;
  customer_code?: string;
  country_prefix?: string;
}

interface ClienteSearchFieldProps {
  value: string | Cliente;
  onChange: (value: string, clienteData?: Cliente) => void;
  placeholder?: string;
  required?: boolean;
  showAddButton?: boolean;
  disabled?: boolean;
  error?: string;
  onClienteSelect?: (cliente: Cliente | null) => void;
}

export const ClienteSearchField: React.FC<ClienteSearchFieldProps> = ({
  value,
  onChange,
  placeholder = 'Cerca per QR, targa, nome, ragione sociale...',
  required = false,
  showAddButton = true,
  disabled = false,
  error,
  onClienteSelect
}) => {
  const { getToken, user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState<Cliente[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [customerDetailModal, setCustomerDetailModal] = useState<Cliente | null>(null);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Permessi per aggiungere cliente
  const userRole = (user as any)?.role || user?.group_id || (user as any)?.groups?.[0]?.groupId || '';
  const userGroupsArray = (user as any)?.groups?.map((g: any) => g.groupId) || [userRole];
  const canAddCliente = userGroupsArray.some((group: string) => 
    ['superadmin', 'admin', 'accettazione'].includes(group)
  ) || ['superadmin', 'admin', 'accettazione'].includes(userRole);

  // Calcola posizione dropdown
  const updateDropdownPosition = useCallback(() => {
    if (searchInputRef.current) {
      const rect = searchInputRef.current.getBoundingClientRect();
      
      // Use fixed positioning (no need to add scroll offset)
      setDropdownPosition({
        top: rect.bottom + 2, // Small gap below the input
        left: rect.left,
        width: rect.width
      });
    }
  }, []);

  // Cerca clienti nel backend
  const searchClients = useCallback(
    debounce(async (query: string) => {
      if (query.length < 2) {
        setSuggestions([]);
        return;
      }

      setIsLoading(true);
      try {
        const token = getToken();
        const response = await fetch(`/api/accettazione/search/clients?q=${encodeURIComponent(query)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setSuggestions(data.clients || []);
          setShowDropdown(true);
        }
      } catch (err) {
        console.error('Errore ricerca clienti:', err);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    [getToken]
  );

  // Gestione input ricerca
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
    
    if (term.length >= 2) {
      updateDropdownPosition();
      searchClients(term);
    } else {
      setSuggestions([]);
      setShowDropdown(false);
    }
  };

  // Selezione cliente
  const handleSelectCliente = (cliente: Cliente) => {
    setSelectedCliente(cliente);
    setSearchTerm(getClienteDisplayName(cliente));
    setShowDropdown(false);
    setSuggestions([]);
    // Passa l'oggetto cliente completo come value
    onChange(cliente as any, cliente);
    onClienteSelect?.(cliente);
  };

  // Nome visualizzato del cliente - priorità ai dati del profilo
  const getClienteDisplayName = (cliente: Cliente): string => {
    if (cliente.customer_type === 'company') {
      return cliente.company_name || cliente.profile_email || cliente.auth_email;
    } else {
      return `${cliente.name || ''} ${cliente.surname || ''}`.trim() || cliente.profile_email || cliente.auth_email;
    }
  };

  // Get customer phone (handles phone_prefix)
  const getClientePhone = (cliente: Cliente): string | undefined => {
    return cliente.phone;
  };

  // Get customer VAT or Tax code
  const getClienteVatOrTax = (cliente: Cliente): { type: 'vat' | 'tax', value: string } | null => {
    if (cliente.vat_number) return { type: 'vat', value: cliente.vat_number };
    if (cliente.tax_code) return { type: 'tax', value: cliente.tax_code };
    return null;
  };

  // Chiudi dropdown quando clicchi fuori e gestisci resize/scroll
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    const handleResize = () => {
      if (showDropdown) {
        updateDropdownPosition();
      }
    };

    const handleScroll = () => {
      if (showDropdown) {
        // Check if the input field is still visible in viewport
        if (searchInputRef.current) {
          const rect = searchInputRef.current.getBoundingClientRect();
          const isVisible = rect.top >= 0 && rect.top <= window.innerHeight - 100;
          
          if (isVisible) {
            updateDropdownPosition();
          } else {
            // Close dropdown if input is out of view
            setShowDropdown(false);
          }
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [showDropdown, updateDropdownPosition]);

  // Sincronizza stato interno quando value cambia esternamente (edit mode)
  useEffect(() => {
    if (value === undefined) return; // Skip re-render temporanei

    if (value === null || value === '') {
      setSearchTerm('');
      setSelectedCliente(null);
    } else if (typeof value === 'object' && value.id) {
      const clienteObj = value as Cliente;
      // Aggiorna solo se diverso per evitare loop
      if (selectedCliente?.id !== clienteObj.id) {
        setSelectedCliente(clienteObj);
        setSearchTerm(getClienteDisplayName(clienteObj));
        onClienteSelect?.(clienteObj);
      }
    }
    // Ignora stringhe ID - gestiamo solo oggetti cliente
  }, [value]);

  return (
    <div className="relative">
      <div className="flex gap-2">
        {/* Campo di ricerca */}
        <div className="flex-1 relative">
          {/* Campo hidden per la validazione quando cliente è selezionato */}
          {selectedCliente && (
            <input
              type="hidden"
              value={selectedCliente.id}
              required={required}
              name="clienteSearch_hidden"
            />
          )}
          
          <input
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder={placeholder}
            disabled={disabled}
            required={required && !selectedCliente}
            className={`
              w-full px-3 py-2 bg-slate-700 border rounded-lg text-white 
              placeholder-slate-400 focus:border-blue-500 focus:ring-1 
              focus:ring-blue-500 transition-colors
              ${showAddButton && canAddCliente ? 'pr-24' : 'pr-10'}
              ${error ? 'border-red-500' : 'border-slate-600'}
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          />
          
          {/* Pulsante Nuovo Cliente e Icona ricerca */}
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
            {/* Pulsante Nuovo Cliente */}
            {showAddButton && canAddCliente && (
              <button
                type="button"
                onClick={() => setShowCreateModal(true)}
                className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs transition-colors flex items-center gap-1"
                title="Aggiungi nuovo cliente"
              >
                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Nuovo</span>
              </button>
            )}
            
            {/* Icona ricerca o loading */}
            {isLoading ? (
              <svg className="animate-spin h-5 w-5 text-slate-400" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            )}
          </div>

          {/* Dropdown suggerimenti */}
          {showDropdown && suggestions.length > 0 && (
            <div
              ref={dropdownRef}
              className="fixed z-[999] bg-slate-800 border border-slate-600 rounded-lg shadow-lg max-h-80 overflow-y-auto"
              style={{
                position: 'fixed',
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
                zIndex: 999
              }}
            >
              {suggestions.map((cliente) => {
                const phone = getClientePhone(cliente);
                const vatOrTax = getClienteVatOrTax(cliente);

                return (
                  <div
                    key={cliente.id}
                    className="flex items-stretch hover:bg-slate-700/50 transition-all border-b border-slate-700 last:border-b-0"
                  >
                    {/* Area cliccabile principale */}
                    <button
                      type="button"
                      onClick={() => handleSelectCliente(cliente)}
                      className="flex-1 text-left p-3 min-w-0"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          {/* Nome principale */}
                          <p className="font-medium text-white truncate">{getClienteDisplayName(cliente)}</p>

                          {/* Email */}
                          <p className="text-sm text-gray-400 truncate">{cliente.profile_email || cliente.auth_email}</p>

                          {/* Riga info aggiuntive con badge colorati */}
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
                            {/* Telefono */}
                            {phone && (
                              <span className="text-xs text-blue-400 flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {cliente.phone_prefix || ''} {phone}
                              </span>
                            )}

                            {/* Città */}
                            {cliente.city && (
                              <span className="text-xs text-emerald-400 flex items-center gap-1">
                                <MapPin className="w-3 h-3" /> {cliente.city}{cliente.province ? ` (${cliente.province})` : ''}
                              </span>
                            )}

                            {/* P.IVA o CF */}
                            {vatOrTax && (
                              <span className="text-xs text-purple-400 flex items-center gap-1">
                                <Building2 className="w-3 h-3" /> {vatOrTax.type === 'vat' ? `P.IVA ${vatOrTax.value}` : `CF ${vatOrTax.value}`}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Badge stato */}
                        <span className={`flex-shrink-0 px-2 py-1 rounded text-xs font-medium ${
                          cliente.source === 'temporary'
                            ? 'bg-amber-500/20 text-amber-400'
                            : 'bg-green-500/20 text-green-400'
                        }`}>
                          {cliente.source === 'temporary' ? '⏳ Temporaneo' : '✓ Confermato'}
                        </span>
                      </div>
                    </button>

                    {/* Pulsante dettagli */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCustomerDetailModal(cliente);
                      }}
                      className="px-3 flex items-center justify-center bg-slate-700/30 hover:bg-blue-500/20 border-l border-slate-700 transition-colors"
                      title="Visualizza dettagli cliente"
                    >
                      <Eye className="w-4 h-4 text-gray-400 hover:text-blue-400" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>

      {/* Messaggio errore */}
      {error && (
        <div className="mt-1 text-sm text-red-400">
          {error}
        </div>
      )}


      {/* Cliente selezionato */}
      {selectedCliente && (() => {
        const phone = getClientePhone(selectedCliente);
        const vatOrTax = getClienteVatOrTax(selectedCliente);

        return (
          <div className="mt-4 bg-green-500/10 border border-green-500/30 rounded-xl p-4">
            <div className="flex items-start justify-between gap-4">
              {/* Area cliccabile per aprire dettagli */}
              <button
                type="button"
                onClick={() => setCustomerDetailModal(selectedCliente)}
                className="flex items-start gap-3 flex-1 min-w-0 text-left hover:bg-green-500/10 rounded-lg -m-2 p-2 transition-colors"
                title="Clicca per visualizzare dettagli cliente"
              >
                <div className="w-6 h-6 flex-shrink-0 mt-0.5">
                  <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-white">{getClienteDisplayName(selectedCliente)}</p>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      selectedCliente.source === 'temporary'
                        ? 'bg-amber-500/20 text-amber-400'
                        : 'bg-green-500/20 text-green-400'
                    }`}>
                      {selectedCliente.source === 'temporary' ? '⏳ Temporaneo' : '✓ Confermato'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-400">{selectedCliente.profile_email || selectedCliente.auth_email}</p>

                  {/* Info aggiuntive con badge colorati */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5">
                    {phone && (
                      <span className="text-xs text-blue-400 flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {selectedCliente.phone_prefix || ''} {phone}
                      </span>
                    )}
                    {selectedCliente.city && (
                      <span className="text-xs text-emerald-400 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {selectedCliente.city}{selectedCliente.province ? ` (${selectedCliente.province})` : ''}
                      </span>
                    )}
                    {vatOrTax && (
                      <span className="text-xs text-purple-400 flex items-center gap-1">
                        <Building2 className="w-3 h-3" /> {vatOrTax.type === 'vat' ? `P.IVA ${vatOrTax.value}` : `CF ${vatOrTax.value}`}
                      </span>
                    )}
                  </div>
                </div>
              </button>

              {/* Pulsanti azione */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Pulsante conferma cliente temporaneo */}
                {selectedCliente.source === 'temporary' && (
                  <button
                    type="button"
                    onClick={() => {
                      // Apri direttamente il form di conferma cliente in nuova tab
                      const confirmUrl = `/admin/profile-form?tempCustomerId=${selectedCliente.id}`;
                      window.open(confirmUrl, '_blank');

                      // Quando l'utente torna, verifica se il cliente è stato confermato
                      const checkConfirmation = async () => {
                        try {
                          const token = getToken();
                          const response = await fetch(`/api/accettazione/search/clients?q=${encodeURIComponent(selectedCliente.auth_email || '')}`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                          });
                          if (response.ok) {
                            const data = await response.json();
                            const updatedCliente = data.clients?.find((c: Cliente) =>
                              c.auth_email === selectedCliente.auth_email ||
                              c.profile_email === selectedCliente.profile_email
                            );
                            if (updatedCliente && updatedCliente.source === 'definitive') {
                              // Cliente confermato! Aggiorna lo stato
                              setSelectedCliente(updatedCliente);
                              onChange(updatedCliente as any, updatedCliente);
                              onClienteSelect?.(updatedCliente);
                            }
                          }
                        } catch (err) {
                          console.error('Errore verifica conferma cliente:', err);
                        }
                      };

                      // Controlla quando l'utente torna su questa finestra
                      const handleFocus = () => {
                        checkConfirmation();
                        window.removeEventListener('focus', handleFocus);
                      };
                      window.addEventListener('focus', handleFocus);
                    }}
                    className="text-amber-400 hover:text-amber-300 transition-colors"
                    title="Conferma cliente temporaneo (apre in nuova scheda)"
                  >
                    <UserCheck className="w-5 h-5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setSelectedCliente(null);
                    setSearchTerm('');
                    setSuggestions([]);
                    onChange('');
                    onClienteSelect?.(null);
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                  title="Rimuovi selezione"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal creazione cliente */}
      {showCreateModal && (
        <UserCreateModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSave={() => {
            setShowCreateModal(false);
            // Ricarica ricerca se necessario
            if (searchTerm) {
              searchClients(searchTerm);
            }
          }}
        />
      )}

      {/* Modal Dettagli Cliente */}
      {customerDetailModal && (() => {
        const customer = customerDetailModal;
        const phone = getClientePhone(customer);
        // @ts-ignore - Disponibile per uso futuro nel modal
        const _vatOrTax = getClienteVatOrTax(customer);

        return (
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setCustomerDetailModal(null)}
          >
            <div
              className="bg-slate-800 border border-gray-600/50 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                      <User className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">{getClienteDisplayName(customer)}</h3>
                      <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium ${
                        customer.source === 'temporary'
                          ? 'bg-amber-500/30 text-amber-200'
                          : 'bg-green-500/30 text-green-200'
                      }`}>
                        {customer.source === 'temporary' ? '⏳ Cliente Temporaneo' : '✓ Cliente Confermato'}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCustomerDetailModal(null)}
                    className="text-white/70 hover:text-white transition-colors"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="p-6 space-y-3 max-h-[60vh] overflow-y-auto">
                {/* Sezione Contatti */}
                <div className="text-xs text-gray-300 uppercase tracking-wide font-semibold mb-2">Contatti</div>

                {/* Email */}
                {(customer.profile_email || customer.auth_email) && (
                  <div className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg">
                    <Mail className="w-5 h-5 text-blue-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-400 uppercase tracking-wide">Email</p>
                      <p className="text-white truncate">{customer.profile_email || customer.auth_email}</p>
                    </div>
                  </div>
                )}

                {/* PEC */}
                {customer.pec && (
                  <div className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg">
                    <Mail className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-400 uppercase tracking-wide">PEC</p>
                      <p className="text-white truncate">{customer.pec}</p>
                    </div>
                  </div>
                )}

                {/* Telefono */}
                {phone && (
                  <div className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg">
                    <Phone className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide">Telefono</p>
                      <p className="text-white">{customer.phone_prefix || '+39'} {phone}</p>
                    </div>
                  </div>
                )}

                {/* Sezione Indirizzo */}
                {(customer.street || customer.city) && (
                  <>
                    <div className="text-xs text-gray-300 uppercase tracking-wide font-semibold mb-2 mt-4">Indirizzo</div>

                    {/* Via */}
                    {customer.street && (
                      <div className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg">
                        <Home className="w-5 h-5 text-amber-400 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-gray-400 uppercase tracking-wide">Via</p>
                          <p className="text-white">{customer.street}</p>
                        </div>
                      </div>
                    )}

                    {/* Città */}
                    {customer.city && (
                      <div className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg">
                        <MapPin className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-gray-400 uppercase tracking-wide">Località</p>
                          <p className="text-white">
                            {customer.postal_code ? `${customer.postal_code} ` : ''}{customer.city}{customer.province ? ` (${customer.province})` : ''}
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Sezione Dati Fiscali */}
                <div className="text-xs text-gray-300 uppercase tracking-wide font-semibold mb-2 mt-4">Dati Fiscali</div>

                {/* Tipo cliente */}
                {customer.customer_type && (
                  <div className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg">
                    <Building2 className="w-5 h-5 text-orange-400 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide">Tipo Cliente</p>
                      <p className="text-white">
                        {customer.customer_type === 'company' ? '🏢 Azienda' : '👤 Privato'}
                      </p>
                    </div>
                  </div>
                )}

                {/* P.IVA */}
                {customer.vat_number && (
                  <div className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg">
                    <CreditCard className="w-5 h-5 text-purple-400 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide">Partita IVA</p>
                      <p className="text-white font-mono">{customer.vat_number}</p>
                    </div>
                  </div>
                )}

                {/* Codice Fiscale */}
                {customer.tax_code && (
                  <div className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg">
                    <CreditCard className="w-5 h-5 text-pink-400 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide">Codice Fiscale</p>
                      <p className="text-white font-mono">{customer.tax_code}</p>
                    </div>
                  </div>
                )}

                {/* Codice Univoco SDI */}
                {customer.unique_code && (
                  <div className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg">
                    <Hash className="w-5 h-5 text-indigo-400 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide">Codice Univoco SDI</p>
                      <p className="text-white font-mono">{customer.unique_code}</p>
                    </div>
                  </div>
                )}

                {/* Sezione Sistema */}
                <div className="text-xs text-gray-300 uppercase tracking-wide font-semibold mb-2 mt-4">Sistema</div>

                {/* ID Cliente */}
                <div className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg">
                  <Hash className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-400 uppercase tracking-wide">ID Cliente</p>
                    <p className="text-gray-400 font-mono text-xs truncate">{customer.id}</p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-gray-700/50 flex justify-between gap-3">
                {/* Pulsante Modifica */}
                <button
                  type="button"
                  onClick={() => {
                    // Apri la pagina di modifica in una nuova tab
                    const editUrl = customer.source === 'temporary'
                      ? `/admin/profile-form?tempCustomerId=${customer.id}`
                      : `/admin/profile-form?userId=${customer.id}`;
                    window.open(editUrl, '_blank');
                    setCustomerDetailModal(null);
                  }}
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-600 rounded-lg text-white font-medium transition-colors flex items-center gap-2"
                  title="Modifica dati cliente"
                >
                  <Pencil className="w-4 h-4" />
                  Modifica
                </button>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      handleSelectCliente(customer);
                      setCustomerDetailModal(null);
                    }}
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white font-medium transition-colors"
                  >
                    Seleziona Cliente
                  </button>
                  <button
                    type="button"
                    onClick={() => setCustomerDetailModal(null)}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white font-medium transition-colors"
                  >
                    Chiudi
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};